import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Activity, LayoutDashboard, Users, PieChart, Lightbulb,
  CalendarCheck, Settings, LogOut, MessageSquareText, Menu, X, Plus, ShieldCheck, FileText,
} from "lucide-react";
import PatientForm from "@/components/PatientForm";
import FeedbackDialog from "@/components/FeedbackDialog";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/patients", label: "Patients", icon: Users },
  { to: "/sources", label: "Sources", icon: PieChart },
  { to: "/insights", label: "Insights", icon: Lightbulb },
  { to: "/challenge", label: "30-Day Challenge", icon: CalendarCheck },
  { to: "/report", label: "Monthly Report", icon: FileText },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-zinc-200 transform transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-full flex flex-col">
          <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-6 w-6 text-emerald-600" strokeWidth={1.5} />
              <span className="font-heading text-lg font-semibold text-zinc-900">PatientFlow</span>
              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">Beta</span>
            </div>
            <button className="lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-close">
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 p-3 space-y-0.5">
            {[...NAV, ...(user?.is_admin ? [{ to: "/admin/feedback", label: "User Feedback", icon: ShieldCheck }] : [])].map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-200 ${
                    isActive ? "bg-emerald-50 text-emerald-800 font-medium" : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                  }`
                }
              >
                <n.icon className="h-4 w-4" strokeWidth={1.75} />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-zinc-200 space-y-2">
            <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={() => setShowFeedback(true)} data-testid="give-feedback-btn">
              <MessageSquareText className="h-4 w-4" strokeWidth={1.75} />
              Give Feedback
            </Button>
            <div className="px-2 py-1.5">
              <div className="text-xs font-medium text-zinc-800 truncate">{user?.clinic_name}</div>
              <div className="text-[11px] text-zinc-500 truncate">{user?.email}</div>
            </div>
            <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-zinc-600" onClick={doLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" strokeWidth={1.75} /> Sign out
            </Button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-zinc-200">
          <div className="flex items-center justify-between px-4 sm:px-6 py-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open">
              <Menu className="h-5 w-5" />
            </button>
            <div className="hidden lg:block text-sm text-zinc-500">
              Hi {user?.physio_name?.split(" ")[0] || "there"} · <span className="text-zinc-700 font-medium">{user?.clinic_name}</span>
            </div>
            <Button onClick={() => setShowAdd(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 gap-1.5" data-testid="add-patient-button">
              <Plus className="h-4 w-4" /> Add New Patient
            </Button>
          </div>
        </header>
        <div className="p-4 sm:p-6 lg:p-8">
          <Outlet context={{ openAdd: () => setShowAdd(true) }} />
        </div>
      </main>

      {showAdd && <PatientForm onClose={() => setShowAdd(false)} />}
      {showFeedback && <FeedbackDialog onClose={() => setShowFeedback(false)} />}
    </div>
  );
}
